import { motion } from "framer-motion";
import { BOT_TIER_KEYS, INVESTMENT_TIER_KEYS, LOAN_CAPS, BOT_TIER_META, INVESTMENT_TIER_META } from "../lib/api";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";

const BOT_FEATURES = {
  starter: ["Unlocks real investing", "Unlock AI trading terminal", `Loan eligibility up to $${LOAN_CAPS.starter.toLocaleString()}`, "24/7 bot access"],
  growth: ["Unlocks real investing", "Priority AI Bot", `Loan eligibility up to $${LOAN_CAPS.growth.toLocaleString()}`, "Dedicated advisor"],
  premium: ["Unlocks real investing", "Premium AI engine", `Loan eligibility up to $${LOAN_CAPS.premium.toLocaleString()}`, "VIP support line"],
  elite: ["Unlocks real investing", "Elite hedge algorithms", `Loan eligibility up to $${LOAN_CAPS.elite.toLocaleString()}`, "Private analyst"],
  platinum: ["Unlocks real investing", "Bespoke AI strategy", `Loan eligibility up to $${LOAN_CAPS.platinum.toLocaleString()}`, "Personal portfolio manager"],
};

const INVESTMENT_FEATURES = {
  starter: ["Real trading capital", "30-day cycle", "Entry-level ROI rate", "Great way to start"],
  growth: ["Real trading capital", "30-day cycle", "Stronger ROI rate", "Can use approved loan credit"],
  elite: ["Real trading capital", "30-day cycle", "Elite ROI rate", "Advanced trading allocation"],
  elite_plus: ["Real trading capital", "30-day cycle", "Elite Plus ROI rate", "Priority portfolio handling"],
  elite_pro: ["Real trading capital", "30-day cycle", "Elite Pro ROI rate", "Enhanced trading allocation"],
  platinum: ["Real trading capital", "30-day cycle", "Top-tier ROI rate", "VIP portfolio allocation"],
};

export default function TierGrid({ onSelect, mode = "bot" }) {
  const isInvestment = mode === "investment";
  const keys = isInvestment ? INVESTMENT_TIER_KEYS : BOT_TIER_KEYS;
  const metaSource = isInvestment ? INVESTMENT_TIER_META : BOT_TIER_META;
  const gridCols = isInvestment ? "lg:grid-cols-3" : "lg:grid-cols-5";

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 ${gridCols} gap-6 lg:gap-5 items-stretch`} data-testid="tier-grid">
      {keys.map((k, i) => {
        const t = metaSource[k];
        const features = isInvestment ? INVESTMENT_FEATURES[k] : BOT_FEATURES[k];

        return (
          <motion.div
            key={k}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className={`relative glass rounded-2xl p-7 hover:-translate-y-2 transition-transform duration-500 flex flex-col h-full ${t.glow}`}
            data-testid={`tier-card-${k}`}
          >
            {t.badge && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#D4AF37] text-[#0A0E27] text-[10px] font-bold tracking-widest uppercase rounded-full">
                {t.badge}
              </div>
            )}

            <div className="text-xs uppercase tracking-[0.25em]" style={{ color: t.color }}>{t.name}</div>
            <div className="mt-4 font-display text-4xl font-black">
              ${t.amount.toLocaleString()}{k === "platinum" && <span className="text-lg">+</span>}
            </div>
            <div className="text-[#00D68F] mt-1 text-sm font-semibold">
              {isInvestment ? `${t.roi}% ROI Rate` : "Subscribe to unlock"}
            </div>
            <div className="text-xs text-white/50 mt-1">
              {isInvestment ? "Funded after bot activation" : `Loan cap: $${(LOAN_CAPS[k] || 0).toLocaleString()}`}
            </div>

            <div className="h-px w-full bg-white/10 my-6"></div>

            <ul className="space-y-2.5 text-sm text-white/80 mb-7 flex-grow">
              {features.map((f, j) => (
                <li key={j} className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: t.color }} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            {onSelect ? (
              <button onClick={() => onSelect(k)} className="btn-primary w-full text-center mt-auto" data-testid={`tier-select-${k}`}>
                {isInvestment ? `Fund Real Investment - ${t.name}` : `Activate AI Trading Bot - ${t.name}`}
              </button>
            ) : (
              <Link to="/invest" className="btn-primary w-full text-center block mt-auto" data-testid={`tier-cta-${k}`}>
                {isInvestment ? "Fund Investment" : "Activate Bot"}
              </Link>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}